import React from "react";
import "./Login.css";
import GoogleLogo from "../../components/images/google.png";
import GitbhubLogo from "../../components/images/github.png";
import Chad from "../../components/images/chad.png";
import LoginFluidSmoke from "../../components/LoginFluidSmoke";
import {
    getAuth,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    GithubAuthProvider,
    signInWithPopup
} from "firebase/auth";
import {
    getDatabase,
    ref,
    child,
    update,
    get
} from "firebase/database";
import { useNavigate, Link } from "react-router-dom";
import { FiMail } from "react-icons/fi";
import { LoginPageProps, LoginPageState, InputFieldProps } from "../../types";

const InputField: React.FC<InputFieldProps> = ({ type, value, onChange, placeholder, minLength, required, ...rest }) => (
    <div className="login-badge-field">
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minLength={minLength ? parseInt(minLength, 10) : undefined}
            required={required}
            autoComplete={type === "password" ? "current-password" : "email"}
            {...rest}
        />
    </div>
);

function LoginPageWrapper(props: { darkMode: boolean }) {
    const navigate = useNavigate();
    return <LoginPage {...props} navigate={navigate} />;
}

class LoginPage extends React.Component<LoginPageProps, LoginPageState> {
    constructor(props: LoginPageProps) {
        super(props);
        this.state = {
            email: "",
            password: "",
            error: null,
            isLoading: false,
            newUser: true,
            showAccountTypeModal: false,
            pendingUser: null,
            showEmailForm: false
        };

        this.loginUser = this.loginUser.bind(this);
        this.loginWithGoogle = this.loginWithGoogle.bind(this);
        this.loginWithGithub = this.loginWithGithub.bind(this);
    }

    onChange = (field: keyof LoginPageState) => (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState((prev) => ({
            ...prev,
            [field]: e.target.value,
            error: null
        }));
    }

    handleThirdPartyLogin = (providerInstance: GoogleAuthProvider | GithubAuthProvider): void => {
        const auth = getAuth();
        signInWithPopup(auth, providerInstance).then((result) => {
            const user = result.user;
            const dbRef = ref(getDatabase());

            get(child(dbRef, `users/${user.uid}`)).then((snapshot) => {
                if (snapshot.exists()) {
                    this.props.navigate("/dashboard");
                } else {
                    this.showAccountTypeModal(user);
                }
            }).catch((error) => {
                console.error('Error checking user data:', error);
                this.props.navigate("/dashboard");
            });
        }).catch((error) => {
            console.error(`Error [${error.code}]: ${error.message}`);
            this.setState({ error: error.message || "Sign-in failed" });
        });
    }

    showAccountTypeModal = (user: any): void => {
        this.setState({
            showAccountTypeModal: true,
            pendingUser: user
        });
    }

    hideAccountTypeModal = (): void => {
        this.setState({
            showAccountTypeModal: false,
            pendingUser: null
        });
    }

    completeThirdPartySignup = (): void => {
        const { pendingUser } = this.state;
        const dbRef = ref(getDatabase());

        const userData = {
            email: pendingUser.email,
            metadata: pendingUser.metadata,
            accountType: "job_seeker",
            createdAt: new Date().toISOString()
        };

        update(child(dbRef, `users/${pendingUser.uid}`), userData).then(() => {
            this.hideAccountTypeModal();
            this.props.navigate("/dashboard");
        }).catch((error) => {
            console.error('Error saving user data:', error);
            this.hideAccountTypeModal();
            this.props.navigate("/dashboard");
        });
    }

    redirectAfterLogin = (): void => {
        const prevBoard = localStorage.getItem("prevBoard");
        if (prevBoard) {
            this.props.navigate(`/box/${JSON.parse(prevBoard)}`);
        } else {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const dbRef = ref(getDatabase());
                get(child(dbRef, `users/${user.uid}`)).then((snapshot) => {
                    if (snapshot.exists()) {
                        this.props.navigate("/dashboard");
                    } else {
                        this.props.navigate("/dashboard");
                    }
                }).catch((error) => {
                    console.error('Error getting user data:', error);
                    this.props.navigate("/dashboard");
                });
            } else {
                this.props.navigate("/");
            }
        }
    }

    loginWithGithub = (): void => {
        this.handleThirdPartyLogin(new GithubAuthProvider());
    }

    loginWithGoogle = (): void => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            'login_hint': 'user@example.com'
        });
        this.handleThirdPartyLogin(provider);
    }

    handleLogin = (authFunction: typeof signInWithEmailAndPassword): void => {
        const auth = getAuth();
        const { email, password } = this.state;

        this.setState({ isLoading: true, error: null });
        authFunction(auth, email, password).then(() => {
            this.setState({ isLoading: false });
            this.redirectAfterLogin();
        }).catch((error) => {
            console.error(`Error [${error.code}]: ${error.message}`);
            this.setState({
                isLoading: false,
                error: error.message || "Sign-in failed"
            });
        });
    }

    loginUser = (): void => {
        this.handleLogin(signInWithEmailAndPassword);
    }

    handleEmailFormSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        this.loginUser();
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const { email, password, error, showEmailForm, isLoading } = this.state;
        const pageClass = darkMode ? "login-badge-page login-badge-page--dark" : "login-badge-page";

        return (
            <div className={pageClass} {...({ darkMode } as any)}>
                <div className="login-badge-smoke-layer" aria-hidden={true}>
                    <LoginFluidSmoke />
                </div>

                <div className="login-badge-radial-aside" aria-hidden={true} role="presentation" />

                <div className="login-badge-main">
                    <Link to="/" className="login-badge-back">
                        ← Interview Sandbox
                    </Link>

                    <div className="login-badge-center">
                        <div className="login-badge-wrap">
                            <div className="login-badge-card">
                            <div className="login-badge-card-top">
                                <div className="login-badge-heading-row">
                                    <img
                                        src={Chad}
                                        alt=""
                                        className="login-badge-brand-icon"
                                        width={40}
                                        height={40}
                                    />
                                    <h1 className="login-badge-title">Interview Sandbox</h1>
                                </div>
                                <p className="login-badge-subtitle">Sign in to continue</p>
                            </div>

                            {error && (
                                <div className="login-badge-error" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="login-badge-actions">
                                <button
                                    type="button"
                                    className="login-badge-btn login-badge-btn-google"
                                    onClick={this.loginWithGoogle}
                                    disabled={isLoading}
                                >
                                    <img src={GoogleLogo} alt="" className="login-badge-oauth-icon" />
                                    Continue with Google
                                </button>

                                <button
                                    type="button"
                                    className="login-badge-btn login-badge-btn-github"
                                    onClick={this.loginWithGithub}
                                    disabled={isLoading}
                                >
                                    <img src={GitbhubLogo} alt="" className="login-badge-oauth-icon" />
                                    Continue with GitHub
                                </button>

                                <div className="login-badge-divider">
                                    <span>or</span>
                                </div>

                                <div className={`login-badge-email-section ${showEmailForm ? "login-badge-email-section--open" : ""}`}>
                                    <div className="login-badge-email-toggle-wrap">
                                        <div className="login-badge-email-toggle-inner">
                                            <button
                                                type="button"
                                                className="login-badge-btn login-badge-btn-email"
                                                onClick={() => {
                                                    this.setState({ showEmailForm: true, error: null }, () => {
                                                        window.setTimeout(() => {
                                                            document.querySelector<HTMLInputElement>(
                                                                ".login-badge-email-form input[type=email]"
                                                            )?.focus();
                                                        }, 420);
                                                    });
                                                }}
                                                disabled={isLoading}
                                                tabIndex={showEmailForm ? -1 : 0}
                                                aria-hidden={showEmailForm}
                                            >
                                                <FiMail className="login-badge-mail-icon" aria-hidden />
                                                Continue with Email
                                            </button>
                                        </div>
                                    </div>
                                    <div className="login-badge-email-form-shell">
                                        <div
                                            className="login-badge-email-form-shell-inner"
                                            {...(!showEmailForm ? { inert: true } : {})}
                                        >
                                            <form className="login-badge-email-form" onSubmit={this.handleEmailFormSubmit}>
                                                <InputField
                                                    type="email"
                                                    value={email}
                                                    onChange={this.onChange('email')}
                                                    placeholder="Email"
                                                    required
                                                />
                                                <InputField
                                                    type="password"
                                                    value={password}
                                                    onChange={this.onChange('password')}
                                                    placeholder="Password"
                                                    minLength="8"
                                                    required
                                                />
                                                <button
                                                    type="submit"
                                                    className="login-badge-btn login-badge-btn-submit"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? "Signing in…" : "Sign in"}
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="login-badge-links">
                                <p>
                                    Don&apos;t have an account?{" "}
                                    <Link to="/signup" className="login-badge-link">Sign up</Link>
                                </p>
                                <p>
                                    <Link to="/forgot-password" className="login-badge-link">Forgot your password?</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                    </div>

                    <p className="login-badge-legal">
                        Continuing, you agree to responsible use of the platform. See{" "}
                        <a href="https://interviewsandbox.com" target="_blank" rel="noopener noreferrer" className="login-badge-link-muted">interviewsandbox.com</a>
                        {" "}for updates.
                    </p>
                </div>

                {this.state.showAccountTypeModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h3>Welcome!</h3>
                            <p>Your account has been created successfully. You can now access your dashboard.</p>

                            <div className="modal-actions">
                                <button type="button" className="modal-btn confirm" onClick={this.completeThirdPartySignup}>
                                    Continue to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default LoginPageWrapper;
